import {
  fetchToken,
  fetchUser,
  fetchLogout,
  fetchLineToken,
  fetchLineRegister,
  fetchMghrwcLogin,
  fetchLineDelete,
  fetchLineRefresh
} from '@/services/userService';
import { clearLoginShareholderSession } from '@/lib/functions';

const alignmentAlertStorageKey = (account: string) => `hideAlignmentAlert_${account}`;
const ALIGNMENT_ALERT_SESSION_KEY = 'alignmentAlertBrowserSession';
const ALIGNMENT_ALERT_HEARTBEAT_KEY = 'alignmentAlertBrowserSessionHeartbeat';
/** 他タブの生存判定。これより長い間 heartbeat が無ければブラウザ再起動とみなす */
const ALIGNMENT_ALERT_SESSION_TTL_MS = 3000;

let alignmentAlertHeartbeatTimer: ReturnType<typeof setInterval> | null = null;

function generateAlignmentAlertSessionId(): string {
  return `${Date.now().toString(36)}_${Math.random().toString(36).slice(2)}`;
}

function touchAlignmentAlertHeartbeat(): void {
  localStorage.setItem(ALIGNMENT_ALERT_HEARTBEAT_KEY, String(Date.now()));
}

function startAlignmentAlertHeartbeat(): void {
  touchAlignmentAlertHeartbeat();
  if (alignmentAlertHeartbeatTimer != null) {
    return;
  }
  alignmentAlertHeartbeatTimer = setInterval(touchAlignmentAlertHeartbeat, 1000);
}

/**
 * ブラウザセッションIDを返す。
 * - 同一ブラウザ内の別タブ: localStorage の heartbeat が生きていれば同じIDを共有
 * - ブラウザ再起動後: heartbeat が切れているため新しいIDになり、閉じた状態は無効化される
 */
function getAlignmentAlertBrowserSessionId(): string {
  const existing = sessionStorage.getItem(ALIGNMENT_ALERT_SESSION_KEY);
  if (existing) {
    localStorage.setItem(ALIGNMENT_ALERT_SESSION_KEY, existing);
    startAlignmentAlertHeartbeat();
    return existing;
  }

  const sharedId = localStorage.getItem(ALIGNMENT_ALERT_SESSION_KEY);
  const heartbeat = Number(localStorage.getItem(ALIGNMENT_ALERT_HEARTBEAT_KEY) || 0);
  const isAlive =
    !!sharedId && Date.now() - heartbeat < ALIGNMENT_ALERT_SESSION_TTL_MS;

  const sessionId = isAlive ? sharedId! : generateAlignmentAlertSessionId();
  sessionStorage.setItem(ALIGNMENT_ALERT_SESSION_KEY, sessionId);
  localStorage.setItem(ALIGNMENT_ALERT_SESSION_KEY, sessionId);
  startAlignmentAlertHeartbeat();
  return sessionId;
}

export interface AuthState {
  token: string | null
  account: string | null
  lang: string | null
  nameJa: string | null
  nameEn: string | null
  group: string | null
  id: number | null
  movie: string | null
  lastLoggedin: number | null
  visited: boolean
  line: LineUser | null
  mghrwc: MghrwcUser | null
  hideAlignmentAlert: boolean
}
export const Auth = {
  namespaced: true,
  state(): AuthState {
    return {
      token: null,
      account: null,
      lang: null,
      nameJa: null,
      nameEn: null,
      group: null,
      id: null,
      movie: null,
      lastLoggedin: null,
      visited: false,
      line: null,
      // line: {
      //   id_token: 'aaaa',
      //   access_token: 'bbbbb'
      // },
      mghrwc: null,
      // mghrwc: {
      //   clubno: 'cccc',
      //   rank: 'string',
      //   corpid: 'string',
      // },
      hideAlignmentAlert: false,
    }
  },
  getters: {
    token: (state: any) => {
      return state.token;
    },
    lang: (state: any) => {
      return state.lang;
    },
    name: (state: any) => {
      return state.nameJa;
    },
    nameEn: (state: any) => {
      return state.nameEn;
    },
    account: (state: any) => {
      return state.account;
    },
    group: (state: any) => {
      return state.group;
    },
    id: (state: any) => {
      return state.id;
    },
    movie: (state: any) => {
      return state.movie;
    },
    lastLoggedin: (state: any) => {
      return state.lastLoggedin;
    },
    visited: (state: any) => {
      return state.visited;
    },
    line: (state: any) => {
      return state.line;
    },
    mghrwc: (state: any) => {
      return state.mghrwc;
    },
    hideAlignmentAlert: (state: any) => {
      return state.hideAlignmentAlert;
    },
    isWAlignmentComplete: (state: any) => {
      return state.line !== null && state.mghrwc !== null;
    },
    /** 株主グループ（subscriber11 / subscriber13）かどうか */
    isShareholder: (state: any) => {
      // ↓ 開発用・後で削除する
      // if (state.account === 'mfhsps1350') {
      //   return true;
      // }
      return state.group === 'subscriber11' || state.group === 'subscriber13';
    },
  },
  mutations: {
    // login: (state: AuthState, token: string | null) => {
    //   state.token = token;
    // },
    setToken: (state: AuthState, token: string | null) => {
      state.token = token;
    },
    updateId: (state: AuthState, id: number | null) => {
      state.id = id;
    },
    updateMovie: (state: AuthState, movie: string | null) => {
      state.movie = `${movie}`;
    },
    removeLastLoggedin: (state: AuthState) => {
      state.lastLoggedin = null;
      localStorage.removeItem('lastLoggedin');
    },
    updateLastLoggedin: (state: AuthState) => {
      state.lastLoggedin = Number(localStorage.lastLoggedin);
    },
    setLastLoggedin: (state: AuthState) => {
      const date = new Date();
      state.lastLoggedin = Number(date.getTime());
      localStorage.lastLoggedin = String(date.getTime());
    },
    logout: (state: AuthState) => {
      if (state.account) {
        localStorage.removeItem(alignmentAlertStorageKey(state.account));
      }
      state.token = null;
      state.nameJa = null;
      state.nameEn = null;
      state.account = null;
      state.group = null;
      state.movie = null;
      state.line = null;
      state.mghrwc = null;
      state.hideAlignmentAlert = false;
    },
    updateLang: (state: AuthState, lang: string | null) => {
      state.lang = lang;
    },
    updateName: (state: AuthState, name: {
      ja: string | null;
      en: string | null;
    }) => {
      state.nameJa = name.ja;
      state.nameEn = name.en;
    },
    updateAccount: (state: AuthState, str: string | null) => {
      state.account = str;
    },
    updateGroup: (state: AuthState, group: string | null) => {
      state.group = group;
    },
    updateVisited: (state: AuthState, bool: boolean) => {
      state.visited = bool;
    },
    updateLine: (state: AuthState, line: LineUser | null) => {
      state.line = line;
    },
    updateMghrwc: (state: AuthState, mghrwc: MghrwcUser | null) => {
      state.mghrwc = mghrwc;
    },
    hideAlignmentAlert: (state: AuthState) => {
      state.hideAlignmentAlert = true;
      if (state.account) {
        localStorage.setItem(
          alignmentAlertStorageKey(state.account),
          getAlignmentAlertBrowserSessionId()
        );
      }
    },
    restoreHideAlignmentAlert: (state: AuthState) => {
      if (!state.account) {
        state.hideAlignmentAlert = false;
        return;
      }
      const hiddenForSession = localStorage.getItem(
        alignmentAlertStorageKey(state.account)
      );
      state.hideAlignmentAlert =
        hiddenForSession === getAlignmentAlertBrowserSessionId();
    },
  },
  actions: {
    login: async (cxt: any, param: TokenParams) => {
      const res = await fetchToken(param)
      .then( (response) => {
        if (response.redirectUrl) {
          location.href = response.redirectUrl;
        } else if (!response.redirectUrl) {
          cxt.commit('setToken', response.token);
          cxt.commit('setLastLoggedin');
          cxt.dispatch('search/historyInit', null, {root: true});
          cxt.dispatch('favorites/init', null, {root: true});
          cxt.dispatch('history/init', null, {root: true});
        }
        return response;
      })
      // .catch( (error) => {
      //   console.error(error.message)
        // return false;
      // });
      return res;
    },
    logout: async (cxt: any) => {
      return await fetchLogout()
      .then( (data) => {
        cxt.commit('logout');
        cxt.commit('removeLastLoggedin');
        cxt.commit('search/logout', null, {root: true});
        cxt.commit('favorites/reset', null, {root: true});
        cxt.commit('history/reset', null, {root: true});
        clearLoginShareholderSession();
        if (sessionStorage.getItem("first-banner")) {
          sessionStorage.clear();
        }
        return data;
      })
    },
    updateUser: async (cxt: any ): Promise<ApiLogin | void> => {
      return await fetchUser()
      .then( (response) => {
        if (response.redirectUrl) {
          location.href = response.redirectUrl;
        } else {
          cxt.commit('setToken', response.token);
          cxt.commit('updateLastLoggedin');
          cxt.commit('updateName', {ja: response.username_ja, en: response.username_en});
          cxt.commit('updateAccount', response.account);
          cxt.commit('updateGroup', response.group);
          cxt.commit('updateId', response.unique_user_no);
          cxt.commit('updateMovie', response.movie);
          cxt.commit('updateLine', response.line || null);
          cxt.commit('updateMghrwc', response.MGHRWC || null);
          cxt.commit('restoreHideAlignmentAlert');
          cxt.dispatch('search/historyInit', null, {root: true});
          cxt.dispatch('favorites/init', null, {root: true});
          cxt.dispatch('history/init', null, {root: true});
        }
        return response;
      })
      // .catch( async (error) => {
      //   console.error('updateUser Error',error)
      //   // throw error;
      //   await cxt.dispatch('logout')
      //   // return false;
      // })
    },
    isLoggedin: async (cxt: any, param: any) => {
      try {
        if (cxt.getters.token) {
          return true;
        }
        const {token} = await fetchUser()
        if (!token) {
          return false;
        }
        cxt.commit('setToken', token);
        await cxt.dispatch('updateUser');
        return true;
      } catch (e) {
        console.error(e)
        throw e;
      }
    },
    autoLogin: async (cxt: any, param: {
      line_id_token?: string;
      line_access_token?: string;
    }) => {

      cxt.commit('updateVisited', true);

      let params: any = {}

      if (!param.line_id_token) {
        console.error('パラメータが見つかりません');
        return false;
      }
      // alert(`line_access_token: ${param.line_access_token}`);

      const resLineToken = await fetchLineToken(param.line_id_token, param.line_access_token)

      if (!resLineToken.token) {
        return false;
      }

      cxt.commit('setToken', resLineToken.token)
      return resLineToken;
    },
    lineRegister: async (cxt: any, accessToken: string) => {
      const token = cxt.getters['token'];
      if (!token) {
        throw {
          status: 0,
          message: 'Please log in'
        }
      }
      let result = null;
      result = await fetchLineRegister(accessToken)
      cxt.dispatch('updateUser')
      return result;
    },
    lineDelete: async (cxt: any, accessToken: string) => {
      await fetchLineDelete()
      .then((data)=>{
        cxt.commit('updateLine', null);
        cxt.commit('updateMghrwc', null);
      })
      cxt.dispatch('updateUser')
    },
    lineRefresh: async (cxt: any, accessToken: string) => {
      return await fetchLineRefresh()
      .then((data)=>{
        cxt.dispatch('updateUser')
        return "success";
      })
      .catch((e)=>{
        throw new Error('アクセストークン更新に失敗しました')
      })
    },
    mghrwcLogin: async (cxt: any, { accessToken, mail, password}: {
      accessToken: string;
      mail: string;
      password: string;
    }) => {
      const token = cxt.getters['token'];
      if (!token) {
        throw {
          status: 0,
          message: 'Please log in'
        }
      }
      let result = null;
      result = await fetchMghrwcLogin({mghrwc_mail: mail, mghrwc_pw: password})
      cxt.dispatch('updateUser')
      return result;
    },
  },
};
